import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiLock, FiUser, FiCopy, FiSave, FiKey } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { authApi } from '../../api/smartTokenizerApi';
import { getStoredSession } from '../../api/authStorage';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import { copyTextToClipboard } from '../../blockchain/offlineSigning';
import { setOfflineSignerAddressForUser } from '../../api/offlineSignerPreferences';
import {
  WalletInputRow,
  GenerateWalletBtn,
  OneTimeKeyRevealDialog,
  useOneTimeWalletReveal
} from '../shared/oneTimeWalletKeyReveal';

const Page = styled.div`
  max-width: 560px;
  margin: 0 auto;
  padding: 2rem;
  min-height: calc(100vh - 80px);
`;

const Title = styled.h1`
  font-size: 1.75rem;
  margin-bottom: 0.5rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: #4facfe;
  }
`;

const Lead = styled.p`
  color: #a0a0a0;
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h2`
  font-size: 1.1rem;
  color: #4facfe;
  margin-bottom: 1rem;
`;

const Field = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    color: #e0e0e0;
    font-size: 0.85rem;
    margin-bottom: 0.35rem;
  }

  input {
    width: 100%;
    max-width: 100%;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    padding: 0.65rem 0.85rem;
    color: #fff;
    font-size: 0.95rem;

    &:focus {
      outline: none;
      border-color: #4facfe;
    }

    &:disabled {
      opacity: 0.6;
    }
  }
`;

const Hint = styled.p`
  font-size: 0.8rem;
  color: #888;
  margin-top: 0.35rem;
  line-height: 1.4;
`;

const Submit = styled.button`
  padding: 0.65rem 1.25rem;
  background: linear-gradient(135deg, #4facfe, #00f2fe);
  color: #0f0f23;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const IconButton = styled.button`
  padding: 0.65rem 1rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #e0e0e0;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover:not(:disabled) {
    background: rgba(79, 172, 254, 0.2);
    border-color: #4facfe;
    color: #fff;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  margin-top: 1rem;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 11000;
  padding: 1rem;
`;

const Modal = styled.div`
  background: #1a1a3e;
  border: 1px solid rgba(79, 172, 254, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  width: 100%;
  max-width: 440px;
`;

const ModalTitle = styled.h3`
  margin-bottom: 1rem;
  color: #fff;
`;

const Denied = styled.div`
  background: rgba(255, 100, 100, 0.08);
  border: 1px solid rgba(255, 100, 100, 0.25);
  border-radius: 12px;
  padding: 1.5rem;
  color: #fecaca;
  line-height: 1.6;
`;

const ProfileRow = styled.div`
  color: #e0e0e0;
  font-size: 0.95rem;
  margin-bottom: 0.5rem;

  strong {
    color: #a0a0a0;
    font-weight: 600;
    margin-right: 0.5rem;
  }
`;

const isValidEvmAddress = (v) => /^0x[a-fA-F0-9]{40}$/.test(String(v || '').trim());

function UserAccount() {
  const hasSession = Boolean(getStoredSession()?.accessToken);
  const { isAdmin } = useTokenIssuer();
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [adminWalletDraft, setAdminWalletDraft] = useState('');
  const [savingWallet, setSavingWallet] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const {
    reveal: adminKeyReveal,
    revealAcknowledged: adminRevealAcknowledged,
    setRevealAcknowledged: setAdminRevealAcknowledged,
    generateWallet: generateAdminWallet,
    dismissKeyReveal: dismissAdminKeyReveal,
    cancelKeyGeneration: cancelAdminKeyGeneration,
    walletFieldEmpty: adminWalletFieldEmpty,
    keyRevealOpen: adminKeyRevealOpen
  } = useOneTimeWalletReveal({
    walletAddress: adminWalletDraft,
    setWalletAddress: setAdminWalletDraft
  });

  const loadProfile = useCallback(async () => {
    setLoadingProfile(true);
    try {
      const { data } = await authApi.getProfile();
      setProfile(data);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to load profile';
      toast.error(msg);
      setProfile(null);
    } finally {
      setLoadingProfile(false);
    }
  }, []);

  useEffect(() => {
    if (hasSession) {
      loadProfile();
    }
  }, [hasSession, loadProfile]);

  useEffect(() => {
    if (!profile || !isAdmin) return;
    const w = profile.walletAddress ?? profile.WalletAddress ?? '';
    setAdminWalletDraft(w);
    const uid = profile.userId ?? profile.UserId;
    if (uid && w) {
      setOfflineSignerAddressForUser(uid, w);
    }
  }, [profile, isAdmin]);

  useEffect(() => {
    if (!isAdmin) dismissAdminKeyReveal();
  }, [isAdmin, dismissAdminKeyReveal]);

  const resetPasswordForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const openChangePassword = () => {
    resetPasswordForm();
    setChangePasswordOpen(true);
  };

  const closeChangePassword = () => {
    setChangePasswordOpen(false);
    resetPasswordForm();
  };

  const handleSaveAdminWallet = async () => {
    const trimmed = adminWalletDraft.trim();
    if (trimmed && !isValidEvmAddress(trimmed)) {
      toast.error('Use a valid address: 0x followed by 40 hex characters.');
      return;
    }
    setSavingWallet(true);
    try {
      const { data } = await authApi.updateMyWalletAddress({ walletAddress: trimmed });
      setProfile(data);
      const uid = data.userId ?? data.UserId ?? profile?.userId ?? profile?.UserId;
      if (uid) {
        setOfflineSignerAddressForUser(uid, trimmed);
      }
      toast.success(trimmed ? 'Public address saved.' : 'Cleared saved address.');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Could not save address';
      toast.error(msg);
    } finally {
      setSavingWallet(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirmation do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await authApi.changePassword({
        currentPassword,
        newPassword
      });
      toast.success('Password updated.');
      closeChangePassword();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Could not change password';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasSession) {
    return (
      <Page>
        <Title>
          <FiUser />
          My account
        </Title>
        <Denied>Sign in with the Login button in the header to manage your account.</Denied>
      </Page>
    );
  }

  const email = profile?.email ?? profile?.Email ?? '';
  const fullName = profile?.fullName ?? profile?.FullName ?? '';
  const org = profile?.organizationName ?? profile?.OrganizationName ?? '';
  const roles = profile?.roles ?? profile?.Roles ?? [];

  return (
    <Page>
      <Title>
        <FiUser />
        My account
      </Title>
      <Lead>View your profile. Use the button below to update your sign-in password.</Lead>

      <Card>
        <CardTitle>Profile</CardTitle>
        {loadingProfile ? (
          <p style={{ color: '#888' }}>Loading…</p>
        ) : (
          <>
            <ProfileRow>
              <strong>Email</strong>
              {email || '—'}
            </ProfileRow>
            <ProfileRow>
              <strong>Name</strong>
              {fullName || '—'}
            </ProfileRow>
            <ProfileRow>
              <strong>Organization</strong>
              {org || '—'}
            </ProfileRow>
            <ProfileRow>
              <strong>Roles</strong>
              {roles.length ? roles.join(', ') : '—'}
            </ProfileRow>
            <Submit type="button" onClick={openChangePassword} disabled={loadingProfile} style={{ marginTop: '1.25rem' }}>
              <FiLock />
              Change password
            </Submit>
          </>
        )}
      </Card>

      {isAdmin && (
        <Card>
          <CardTitle>Blockchain public address</CardTitle>
          <p style={{ color: '#94a3b8', fontSize: '0.88rem', lineHeight: 1.5, marginBottom: '1rem' }}>
            Your admin account&apos;s public EVM address (e.g. the offline signer that pays gas). Stored on the server
            and synced to this browser for deploy / mint forms.
          </p>
          {loadingProfile ? (
            <p style={{ color: '#888' }}>Loading…</p>
          ) : (
            <>
              <OneTimeKeyRevealDialog
                reveal={adminKeyReveal}
                revealAcknowledged={adminRevealAcknowledged}
                onRevealAcknowledgedChange={setAdminRevealAcknowledged}
                onConfirmBackup={dismissAdminKeyReveal}
                onCancelDiscard={cancelAdminKeyGeneration}
                publicAddressCaption="Public address (shown above — save with Save address when ready)"
              />
              <Field>
                <label htmlFor="admin-wallet-address">Wallet address (0x…)</label>
                <WalletInputRow style={{ width: '100%' }}>
                  <input
                    id="admin-wallet-address"
                    value={adminWalletDraft}
                    onChange={(e) => setAdminWalletDraft(e.target.value)}
                    placeholder="0x…"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  {adminWalletFieldEmpty && (
                    <GenerateWalletBtn
                      type="button"
                      onClick={generateAdminWallet}
                      title="Create a new key pair in this browser"
                    >
                      <FiKey aria-hidden />
                      Generate
                    </GenerateWalletBtn>
                  )}
                </WalletInputRow>
                <Hint>
                  Leave empty to clear. Must be 0x + 40 hex digits when set. Paste an existing address, or use{' '}
                  <strong>Generate</strong> to create one — the private key is shown only once; store it offline before
                  saving.
                </Hint>
              </Field>
              <Row style={{ marginTop: 0 }}>
                <Submit
                  type="button"
                  onClick={handleSaveAdminWallet}
                  disabled={savingWallet || adminKeyRevealOpen}
                >
                  <FiSave />
                  {savingWallet ? 'Saving…' : 'Save address'}
                </Submit>
                <IconButton
                  type="button"
                  disabled={!adminWalletDraft.trim() || adminKeyRevealOpen}
                  onClick={async () => {
                    await copyTextToClipboard(adminWalletDraft.trim());
                    toast.success('Copied');
                  }}
                >
                  <FiCopy style={{ verticalAlign: 'middle', marginRight: 6 }} />
                  Copy
                </IconButton>
              </Row>
            </>
          )}
        </Card>
      )}

      {changePasswordOpen && (
        <ModalOverlay
          onClick={(e) => {
            if (e.target !== e.currentTarget || submitting) return;
            if (adminKeyRevealOpen) {
              toast.error('Finish the wallet security dialog first (backup or discard).');
              return;
            }
            closeChangePassword();
          }}
        >
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Change password</ModalTitle>
            <form onSubmit={handleChangePassword}>
              <Field>
                <label>Current password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </Field>
              <Field>
                <label>New password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </Field>
              <Field>
                <label>Confirm new password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </Field>
              <Hint>
                Password must be at least 8 characters and include uppercase, lowercase, and a digit (platform policy).
              </Hint>
              <Row>
                <Submit type="submit" disabled={submitting}>
                  {submitting ? 'Updating…' : 'Update password'}
                </Submit>
                <IconButton type="button" onClick={closeChangePassword} disabled={submitting}>
                  Cancel
                </IconButton>
              </Row>
            </form>
          </Modal>
        </ModalOverlay>
      )}
    </Page>
  );
}

export default UserAccount;
